import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { isNil } from "lodash";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Tab,
  Tabs,
  Paper,
  Box,
  Tooltip,
  Typography
} from "@material-ui/core";

import IconButton from "@material-ui/core/IconButton";
import {
  X as LucideX,
  HelpCircle,
  RefreshCw,
  Copy,
  Info,
  AlertTriangle,
} from "lucide-react";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import CustomToggleSwitch from "../CustomToggleSwitch";
import TabPanel from "../TabPanel";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";
import SimpleDatePicker from "../SimpleDatePicker";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import SchedulesForm from "../SchedulesForm";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const normalizeIntegrationText = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const isNativeFlowBuilderIntegration = integration => {
  const type = normalizeIntegrationText(integration?.type);
  const name = normalizeIntegrationText(integration?.name);

  return type === "flowbuilder" || name === "flowbuilder";
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4
    },

    dialogPaper: {
      ...(ds.dialogPaper || {}),
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(244, 67, 54, 0.1)",
      color: ds.closeButton?.color || "#f44336",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    tabsPaper: {
      flexShrink: 0,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
      borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      borderRadius: 0,
      boxShadow: "none",
    },
    tabs: {
      minHeight: 42,
      "& .MuiTab-root": {
        minHeight: 42,
        textTransform: "none",
        fontSize: 13,
        fontWeight: 500,
        minWidth: "auto",
        padding: "6px 18px",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        "&.Mui-selected": {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      },
      "& .MuiTabs-indicator": {
        height: 2,
        borderRadius: 2,
      },
      "& .MuiTabScrollButton-root": {
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      },
    },
    tabContent: {
      background: "transparent",
      boxShadow: "none",
      borderRadius: 0,
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      "& > [role='tabpanel']": {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
      "& .MuiDialogContent-root": {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        borderTop: "none",
        borderBottom: "none",
        padding: "16px 20px",
        ...theme.scrollbarStyles,
      },
    },

    multFieldLine: {
      marginTop: 12,
      display: "flex",
      "& > *:not(:last-child)": {
        marginRight: theme.spacing(1),
      },
    },

    // Mesmo padrão da rota /settings/schedules
    schedulesTabContainer: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },

    btnWrapper: {
      position: "relative",
    },
    importMessage: {
      marginTop: 12,
      marginBottom: 12,
      padding: 12,
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      borderRadius: 12,
      background: isDark ? "rgba(255,255,255,0.025)" : "#fafafa",
      display: "flex",
      flexDirection: "column",
    },
    multFieldLine: {
      marginTop: 8,
      display: "flex",
      flexWrap: "wrap",
      "& > *:not(:last-child)": {
        marginRight: theme.spacing(1),
      },
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      marginTop: 16,
      marginBottom: 4,
    },
    sectionDescription: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 4,
      lineHeight: 1.5,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      cursor: "help",
      opacity: 0.6,
      marginLeft: 4,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    labelWithHelp: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    },
    fieldWithHelp: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      width: "100%",
    },
    fieldWithHelpInner: {
      flex: 1,
      minWidth: 0,
    },
    toggleRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      marginRight: 16,
      marginBottom: 4,
    },
    toggleRowLabel: {
      fontSize: 14,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      lineHeight: 1.4,
      cursor: "pointer",
      userSelect: "none",
    },
    importInfoBox: {
      marginTop: 4,
      marginBottom: 14,
      padding: "12px 14px",
      borderRadius: 10,
      background: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
      border: `1px solid ${isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.20)"}`,
    },
    importInfoTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#90caf9" : "#1565c0",
      marginBottom: 6,
    },
    importInfoText: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
      lineHeight: 1.55,
      "& strong": {
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
      "& ul": {
        margin: "4px 0 4px 18px",
        padding: 0,
      },
      "& li": {
        marginBottom: 2,
      },
    },
    importAlertBox: {
      marginTop: 12,
      padding: "10px 14px",
      borderRadius: 10,
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      background: isDark ? "rgba(244,67,54,0.08)" : "rgba(244,67,54,0.05)",
      border: `1px solid ${isDark ? "rgba(244,67,54,0.25)" : "rgba(244,67,54,0.18)"}`,
      fontSize: 11.5,
      lineHeight: 1.5,
      color: isDark ? "#ef9a9a" : "#c62828",
    },

    buttonProgress: {
      color: green[500],
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },

    textField: {
      marginRight: theme.spacing(1),
      flex: 1,
    },
    tokenRefresh: {
      minWidth: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };
});

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
});

// Helper: label com ícone de ajuda à direita (tooltip ao passar o mouse).
// Use como `label` em FormControlLabel/TextField.
const LabelHelp = ({ classes, text, help }) => (
  <span className={classes.labelWithHelp}>
    {text}
    {help && (
      <Tooltip arrow placement="top" title={help}>
        <span className={classes.helpIcon}>
          <HelpCircle size={14} strokeWidth={2} />
        </span>
      </Tooltip>
    )}
  </span>
);

const WhatsAppModal = ({ open, onClose, whatsAppId, initialChannel = "whatsapp" }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();
  const themeV4 = useTheme();
  const isDark = themeV4.palette.type === "dark";
  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: {
            main: themeV4.palette.primary.main,
            dark: themeV4.palette.primary.dark || themeV4.palette.primary.main,
            contrastText: themeV4.palette.primary.contrastText || "#ffffff",
          },
          background: {
            default: isDark ? (themeV4.palette.darkDialogSurface || "#121212") : "#ffffff",
            paper: isDark ? (themeV4.palette.darkDialogSurface || "#121212") : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
        },
      }),
    [isDark, themeV4.palette.primary.main, themeV4.palette.primary.dark, themeV4.palette.primary.contrastText]
  );
  const [autoToken, setAutoToken] = useState("");
  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    keepFlowsOutOfHours: false,
    ratingMessage: "",
    isDefault: false,
    token: "",
    maxUseBotQueues: 3,
    provider: "beta",
    channel: initialChannel,
    expiresTicket: 0,
    allowGroup: false,
    enableImportMessage: false,
    groupAsTicket: "disabled",
    timeUseBotQueues: '0',
    timeSendQueue: '0',
    sendIdQueue: 0,
    expiresTicketNPS: '0',
    expiresInactiveMessage: "",
    timeInactiveMessage: "",
    inactiveMessage: "",
    maxUseBotQueuesNPS: 3,
    whenExpiresTicket: 0,
    timeCreateNewTicket: 0,
    greetingMediaAttachment: "",
    importRecentMessages: "",
    importOldMessages: "",
    importOldMessagesGroups: "",
    integrationId: "",
    collectiveVacationEnd: "",
    collectiveVacationStart: "",
    collectiveVacationMessage: "",
    queueIdImportMessages: null
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [queues, setQueues] = useState([]);
  const [tab, setTab] = useState("general");
  const [enableImportMessage, setEnableImportMessage] = useState(false);
  const [importOldMessagesGroups, setImportOldMessagesGroups] = useState(false);
  const [closedTicketsPostImported, setClosedTicketsPostImported] = useState(false);
  const [importOldMessages, setImportOldMessages] = useState(moment().add(-1, "days").format("YYYY-MM-DDTHH:mm"));
  const [importRecentMessages, setImportRecentMessages] = useState(moment().add(-1, "minutes").format("YYYY-MM-DDTHH:mm"));
  const [copied, setCopied] = useState(false);

  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [NPSEnabled, setNPSEnabled] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const { user } = useContext(AuthContext);

  const [schedules, setSchedules] = useState([
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
  ]);
  const schedulesRef = useRef(schedules);

  const { get: getSetting } = useCompanySettings();
  const { getPlanCompany } = usePlans();

  const [webhooks, setWebhooks] = useState([]);
  const [flowIdNotPhrase, setFlowIdNotPhrase] = useState();
  const [flowIdWelcome, setFlowIdWelcome] = useState();
  const autoSelectedNativeFlowRef = useRef(false);

  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const externalIntegrations = useMemo(
    () => integrations.filter(integration => !isNativeFlowBuilderIntegration(integration)),
    [integrations]
  );

  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);

  useEffect(() => {
    if (!whatsAppId && !whatsApp.token) {
      setAutoToken(generateRandomCode(30));
    } else if (whatsAppId && !whatsApp.token) {
      setAutoToken(generateRandomCode(30));
    } else {
      setAutoToken(whatsApp.token);
    }
  }, [whatsAppId, whatsApp.token]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      if (planConfigs && planConfigs.plan) {
        setShowIntegrations(planConfigs.plan.useIntegrations);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && !whatsAppId) {
      setWhatsApp(initialState);
      setSelectedIntegration(null);
      setFlowIdNotPhrase();
      setFlowIdWelcome();
      autoSelectedNativeFlowRef.current = false;
    }
  }, [open, whatsAppId, initialChannel]);

  useEffect(() => {
    if (
      !open ||
      whatsAppId ||
      initialChannel !== "whatsapp" ||
      autoSelectedNativeFlowRef.current ||
      flowIdNotPhrase ||
      !webhooks.length
    ) {
      return;
    }

    const defaultFlow =
      webhooks.find(flow => flow.active !== false && flow.status !== false && flow.status !== "inactive") ||
      webhooks[0];

    if (defaultFlow?.id) {
      setFlowIdNotPhrase(defaultFlow.id);
      autoSelectedNativeFlowRef.current = true;
    }
  }, [open, whatsAppId, initialChannel, webhooks, flowIdNotPhrase]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const settingSchedules = await getSetting({ column: "scheduleType" });
        setSchedulesEnabled(settingSchedules?.scheduleType === "connection");
      } catch (err) {
        setSchedulesEnabled(false);
      }
      try {
        const settingNPS = await getSetting({ column: "userRating" });
        setNPSEnabled(settingNPS?.userRating === "enabled");
      } catch (err) {
        setNPSEnabled(false);
      }
    };
    fetchData();
  }, [open, getSetting]);

  const handleEnableImportMessage = async (e) => {
    setEnableImportMessage(e.target.checked);

  };

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        if (data && data?.flowIdNotPhrase) {
          const { data: flowDefault } = await api.get(`flowbuilder/${data.flowIdNotPhrase}`)
          console.log(flowDefault?.flow.id)
          const selectedFlowIdNotPhrase = flowDefault?.flow.id
          setFlowIdNotPhrase(selectedFlowIdNotPhrase)
        }
        if (data && data?.flowIdWelcome) {
          const { data: flowDefault } = await api.get(`flowbuilder/${data.flowIdWelcome}`)
          console.log(flowDefault?.flow.id)
          const selectedFlowIdWelcome = flowDefault?.flow.id
          setFlowIdWelcome(selectedFlowIdWelcome)
        }
        setWhatsApp(data);
        setAutoToken(data.token);
        setSelectedIntegration(data?.integrationId)
        setSchedules(data.schedules)
        if (!isNil(data?.importOldMessages)) {
          setEnableImportMessage(true);
          setImportOldMessages(data?.importOldMessages);
          setImportRecentMessages(data?.importRecentMessages);
          setClosedTicketsPostImported(data?.closedTicketsPostImported);
          setImportOldMessagesGroups(data?.importOldMessagesGroups);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/flowbuilder")
        setWebhooks(data.flows)
      } catch (err) {
        toastError(err)
      }
    })();
  }, [])

  const handleChangeIntegration = (e) => {
    setSelectedIntegration(e.target.value)
  }

  const handleChangeFlowIdNotPhrase = (e) => {
    console.log(e.target.value)
    setFlowIdNotPhrase(e.target.value)
  }

  const handleChangeFlowIdWelcome = (e) => {
    console.log(e.target.value)
    setFlowIdWelcome(e.target.value)
  }

  const handleSaveWhatsApp = async (values) => {
    if (!whatsAppId) setAutoToken(generateRandomCode(30));

    if (NPSEnabled) {

      if (isNil(values.ratingMessage)) {
        toastError(i18n.t("whatsappModal.errorRatingMessage"));
        return;
      }

      if (values.expiresTicketNPS === '0' && values.expiresTicketNPS === '' && values.expiresTicketNPS === 0) {
        toastError(i18n.t("whatsappModal.errorExpiresNPS"));
        return;
      }
    }

    if (values.timeSendQueue === '') values.timeSendQueue = '0'

    if ((values.sendIdQueue === 0 || values.sendIdQueue === '' || isNil(values.sendIdQueue)) && (values.timeSendQueue !== 0 && values.timeSendQueue !== '0')) {
      toastError(i18n.t("whatsappModal.errorSendQueue"));
      return;
    }

    const whatsappData = {
      ...values,
      flowIdWelcome: flowIdWelcome ? flowIdWelcome : null,
      flowIdNotPhrase: flowIdNotPhrase ? flowIdNotPhrase : null,
      integrationId: selectedIntegration ? selectedIntegration : null,
      importOldMessages: enableImportMessage ? importOldMessages : null,
      importRecentMessages: enableImportMessage ? importRecentMessages : null,
      importOldMessagesGroups: importOldMessagesGroups ? importOldMessagesGroups : null,
      closedTicketsPostImported: closedTicketsPostImported ? closedTicketsPostImported : null,
      token: autoToken ? autoToken : null, schedules: schedulesRef.current,
    };

    console.dir(whatsappData)

    delete whatsappData["queues"];
    delete whatsappData["session"];
    try {
      if (whatsAppId) {
        if (whatsAppId && enableImportMessage && whatsApp?.status === "CONNECTED") {
          try {
            setWhatsApp({ ...whatsApp, status: "qrcode" });
            await api.delete(`/whatsappsession/${whatsApp.id}`);
          } catch (err) {
            toastError(err);
          }
        }

        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t("whatsappModal.success"));

      handleClose();
    } catch (err) {
      toastError(err);
    }

  };

  function generateRandomCode(length) {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyvz0123456789";
    let code = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      code += charset.charAt(randomIndex);
    }
    return code;
  }

  const handleRefreshToken = () => {
    setAutoToken(generateRandomCode(30));
  }

  const handleCopyToken = () => {
    navigator.clipboard.writeText(autoToken); // Copia o token para a área de transferência    
    setCopied(true); // Define o estado de cópia como verdadeiro
  };

  const handleSaveSchedules = async (values) => {
    toast.success(i18n.t("whatsappModal.messages.clickSaveToRegister"));
    setSchedules(values);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setEnableImportMessage(false);
    setCopied(false);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {whatsAppId
              ? i18n.t("whatsappModal.title.edit")
              : i18n.t("whatsappModal.title.add")}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            className={classes.closeButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
              e.currentTarget.style.color = "#ffffff";
            }}
          >
            <LucideX size={18} strokeWidth={2.5} />
          </IconButton>
        </div>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue, submitForm }) => (
            <Form>
              <Paper className={classes.tabsPaper} elevation={0}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                  className={classes.tabs}
                >
                  <Tab label={i18n.t("whatsappModal.tabs.general")} value={"general"} />
                  <Tab label={i18n.t("whatsappModal.tabs.integrations")} value={"integrations"} />
                  <Tab label={i18n.t("whatsappModal.tabs.assessments")} value={"nps"} />
                  <Tab label={i18n.t("whatsappModal.tabs.defaultFlow")} value={"flowbuilder"} />
                  {schedulesEnabled && <Tab label={i18n.t("whatsappModal.tabs.schedules")} value={"schedules"} />}
                </Tabs>
              </Paper>
              <Paper className={classes.tabContent} elevation={0}>
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"general"}
                >
                  <DialogContent dividers>
                    {/* SEÇÃO: IDENTIFICAÇÃO */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.identitySectionTitle")}
                    </div>

                    {/* NOME + PADRÃO + GRUPO + TRATAR GRUPOS COMO TICKET */}
                    <div className={classes.multFieldLine}>
                      <Grid spacing={2} container>
                        <Grid item>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid style={{ paddingTop: 15 }} item>
                          <div className={classes.toggleRow}>
                            <CustomToggleSwitch
                              checked={!!values.isDefault}
                              onChange={(e) => setFieldValue("isDefault", e.target.checked)}
                            />
                            <span
                              className={classes.toggleRowLabel}
                              onClick={() => setFieldValue("isDefault", !values.isDefault)}
                            >
                              {i18n.t("whatsappModal.form.default")}
                            </span>
                            <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.defaultDesc")}>
                              <span className={classes.helpIcon}>
                                <HelpCircle size={14} strokeWidth={2} />
                              </span>
                            </Tooltip>
                          </div>
                          <div className={classes.toggleRow}>
                            <CustomToggleSwitch
                              checked={!!values.allowGroup}
                              onChange={(e) => setFieldValue("allowGroup", e.target.checked)}
                            />
                            <span
                              className={classes.toggleRowLabel}
                              onClick={() => setFieldValue("allowGroup", !values.allowGroup)}
                            >
                              {i18n.t("whatsappModal.form.group")}
                            </span>
                            <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.groupDesc")}>
                              <span className={classes.helpIcon}>
                                <HelpCircle size={14} strokeWidth={2} />
                              </span>
                            </Tooltip>
                          </div>
                        </Grid>
                        <Grid xs={6} md={4} item>
                          <div className={classes.fieldWithHelp}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={`${classes.formControl} ${classes.fieldWithHelpInner}`}
                            >
                              <InputLabel id="groupAsTicket-selection-label">
                                {i18n.t("whatsappModal.form.groupAsTicket")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("whatsappModal.form.groupAsTicket")}
                                placeholder={i18n.t("whatsappModal.form.groupAsTicket")}
                                labelId="groupAsTicket-selection-label"
                                id="groupAsTicket"
                                name="groupAsTicket"
                                MenuProps={{
                                  classes: { paper: dropdownClasses.paper },
                                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                  transformOrigin: { vertical: "top", horizontal: "left" },
                                  getContentAnchorEl: null,
                                }}
                              >
                                <MenuItem value={"disabled"}>{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                                <MenuItem value={"enabled"}>{i18n.t("whatsappModal.menuItem.enabled")}</MenuItem>
                              </Field>
                            </FormControl>
                            <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.groupAsTicketDesc")}>
                              <span className={classes.helpIcon}>
                                <HelpCircle size={16} strokeWidth={2} />
                              </span>
                            </Tooltip>
                          </div>
                        </Grid>
                      </Grid>
                    </div>

                    {/* SEÇÃO: IMPORTAÇÃO DE MENSAGENS */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.importSectionTitle")}
                    </div>
                    <div className={classes.importMessage}>
                      {/* Info box com limites e recomendação */}
                      <div className={classes.importInfoBox}>
                        <div className={classes.importInfoTitle}>
                          <Info size={15} strokeWidth={2.2} />
                          {i18n.t("whatsappModal.form.importInfoTitle")}
                        </div>
                        <div className={classes.importInfoText}>
                          <span dangerouslySetInnerHTML={{ __html: i18n.t("whatsappModal.form.importInfoText") }} />
                        </div>
                      </div>

                      <div className={classes.multFieldLine}>
                        <div className={classes.toggleRow}>
                          <CustomToggleSwitch
                            checked={enableImportMessage}
                            onChange={handleEnableImportMessage}
                          />
                          <span
                            className={classes.toggleRowLabel}
                            onClick={() => handleEnableImportMessage({ target: { checked: !enableImportMessage } })}
                          >
                            {i18n.t("whatsappModal.form.importOldMessagesEnable")}
                          </span>
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.importOldMessagesEnableDesc")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={14} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>

                        {enableImportMessage && (
                          <>
                            <div className={classes.toggleRow}>
                              <CustomToggleSwitch
                                checked={importOldMessagesGroups}
                                onChange={(e) => setImportOldMessagesGroups(e.target.checked)}
                              />
                              <span
                                className={classes.toggleRowLabel}
                                onClick={() => setImportOldMessagesGroups(!importOldMessagesGroups)}
                              >
                                {i18n.t("whatsappModal.form.importOldMessagesGroups")}
                              </span>
                              <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.importOldMessagesGroupsDesc")}>
                                <span className={classes.helpIcon}>
                                  <HelpCircle size={14} strokeWidth={2} />
                                </span>
                              </Tooltip>
                            </div>

                            <div className={classes.toggleRow}>
                              <CustomToggleSwitch
                                checked={closedTicketsPostImported}
                                onChange={(e) => setClosedTicketsPostImported(e.target.checked)}
                              />
                              <span
                                className={classes.toggleRowLabel}
                                onClick={() => setClosedTicketsPostImported(!closedTicketsPostImported)}
                              >
                                {i18n.t("whatsappModal.form.closedTicketsPostImported")}
                              </span>
                              <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.closedTicketsPostImportedDesc")}>
                                <span className={classes.helpIcon}>
                                  <HelpCircle size={14} strokeWidth={2} />
                                </span>
                              </Tooltip>
                            </div>
                          </>
                        )}
                      </div>

                      {enableImportMessage && (
                        <Grid style={{ marginTop: 18 }} container spacing={2}>
                          <ThemeProviderV5 theme={v5Theme}>
                            <Grid item xs={12} sm={6}>
                              <SimpleDatePicker
                                label={i18n.t("whatsappModal.form.importOldMessages")}
                                value={importOldMessages ? moment(importOldMessages).toDate() : null}
                                onChange={(date) => {
                                  setImportOldMessages(
                                    date ? moment(date).format("YYYY-MM-DDTHH:mm") : ""
                                  );
                                }}
                                minDate={moment().add(-2, "years").toDate()}
                                maxDate={new Date()}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <SimpleDatePicker
                                label={i18n.t("whatsappModal.form.importRecentMessages")}
                                value={importRecentMessages ? moment(importRecentMessages).toDate() : null}
                                onChange={(date) => {
                                  setImportRecentMessages(
                                    date ? moment(date).format("YYYY-MM-DDTHH:mm") : ""
                                  );
                                }}
                                minDate={importOldMessages ? moment(importOldMessages).toDate() : undefined}
                                maxDate={new Date()}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </ThemeProviderV5>
                          <Grid xs={12} md={12} xl={12} item>
                            <div className={classes.fieldWithHelp}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                className={`${classes.FormControl} ${classes.fieldWithHelpInner}`}
                                fullWidth
                              >
                                <InputLabel id="queueIdImportMessages-selection-label">
                                  {i18n.t("whatsappModal.form.queueIdImportMessages")}
                                </InputLabel>
                                <Field
                                  as={Select}
                                  name="queueIdImportMessages"
                                  id="queueIdImportMessages"
                                  value={values.queueIdImportMessages || '0'}
                                  required={enableImportMessage}
                                  label={i18n.t("whatsappModal.form.queueIdImportMessages")}
                                  placeholder={i18n.t("whatsappModal.form.queueIdImportMessages")}
                                  labelId="queueIdImportMessages-selection-label"
                                  MenuProps={{
                                    classes: { paper: dropdownClasses.paper },
                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                    transformOrigin: { vertical: "top", horizontal: "left" },
                                    getContentAnchorEl: null,
                                  }}
                                >
                                  <MenuItem value={0}>&nbsp;</MenuItem>
                                  {queues.map(queue => (
                                    <MenuItem key={queue.id} value={queue.id}>
                                      {queue.name}
                                    </MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.queueIdImportMessagesDesc")}>
                                <span className={classes.helpIcon}>
                                  <HelpCircle size={16} strokeWidth={2} />
                                </span>
                              </Tooltip>
                            </div>
                          </Grid>
                        </Grid>
                      )}

                      {enableImportMessage && (
                        <div className={classes.importAlertBox}>
                          <AlertTriangle size={15} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
                          <span>{i18n.t("whatsappModal.form.importAlert")}</span>
                        </div>
                      )}
                    </div>

                    {/* SEÇÃO: TOKEN DE INTEGRAÇÃO EXTERNA */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.tokenSectionTitle")}
                    </div>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.token")}
                        type="token"
                        fullWidth
                        value={autoToken}
                        variant="outlined"
                        margin="dense"
                        disabled
                      />
                      <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.tokenSectionDesc")}>
                        <span className={classes.helpIcon}>
                          <HelpCircle size={18} strokeWidth={2} />
                        </span>
                      </Tooltip>
                      <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.tokenRefreshTooltip")}>
                        <IconButton
                          onClick={handleRefreshToken}
                          disabled={isSubmitting}
                          size="small"
                        >
                          <RefreshCw size={18} strokeWidth={2} color="#22c55e" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.tokenCopyTooltip")}>
                        <IconButton
                          onClick={handleCopyToken}
                          size="small"
                        >
                          <Copy size={18} strokeWidth={2} color={copied ? "var(--primaryColor)" : undefined} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* SEÇÃO: REDIRECIONAMENTO DE FILA */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.queueRedirection")}
                    </div>
                    <div className={classes.sectionDescription}>
                      {i18n.t("whatsappModal.form.queueRedirectionDesc")}
                    </div>
                    <Grid spacing={2} container>
                      <Grid xs={6} md={6} item>
                        <div className={classes.fieldWithHelp}>
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            className={`${classes.FormControl} ${classes.fieldWithHelpInner}`}
                            fullWidth
                          >
                            <InputLabel id="sendIdQueue-selection-label">
                              {i18n.t("whatsappModal.form.sendIdQueue")}
                            </InputLabel>
                            <Field
                              as={Select}
                              name="sendIdQueue"
                              id="sendIdQueue"
                              value={values.sendIdQueue || '0'}
                              required={values.timeSendQueue > 0}
                              label={i18n.t("whatsappModal.form.sendIdQueue")}
                              placeholder={i18n.t("whatsappModal.form.sendIdQueue")}
                              labelId="sendIdQueue-selection-label"
                              MenuProps={{
                                classes: { paper: dropdownClasses.paper },
                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                transformOrigin: { vertical: "top", horizontal: "left" },
                                getContentAnchorEl: null,
                              }}
                            >
                              <MenuItem value={0}>&nbsp;</MenuItem>
                              {queues.map(queue => (
                                <MenuItem key={queue.id} value={queue.id}>
                                  {queue.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </FormControl>
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.sendIdQueueDesc")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={16} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>
                      </Grid>

                      <Grid xs={6} md={6} item>
                        <div className={classes.fieldWithHelp}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.timeSendQueue")}
                            fullWidth
                            name="timeSendQueue"
                            variant="outlined"
                            margin="dense"
                            type="number"
                            error={touched.timeSendQueue && Boolean(errors.timeSendQueue)}
                            helperText={touched.timeSendQueue && errors.timeSendQueue}
                            className={classes.fieldWithHelpInner}
                          />
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.timeSendQueueDesc")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={16} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </TabPanel>
                {/* INTEGRAÇÃO */}
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"integrations"}
                >
                  <DialogContent dividers>
                    {/* INFO BOX EXPLICANDO INTEGRAÇÕES */}
                    <div className={classes.importInfoBox}>
                      <div className={classes.importInfoTitle}>
                        <Info size={15} strokeWidth={2.2} />
                        {i18n.t("whatsappModal.form.integrationsInfoTitle")}
                      </div>
                      <div className={classes.importInfoText}>
                        <span dangerouslySetInnerHTML={{ __html: i18n.t("whatsappModal.form.integrationsInfoText") }} />
                      </div>
                    </div>

                    {showIntegrations && (
                      <>
                        {/* SEÇÃO: INTEGRAÇÃO EXTERNA */}
                        <div className={classes.sectionTitle}>
                          {i18n.t("whatsappModal.form.integrationSectionTitle")}
                        </div>
                        <div className={classes.sectionDescription}>
                          {i18n.t("whatsappModal.form.integrationSectionDesc")}
                        </div>
                        <div className={classes.fieldWithHelp}>
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            className={`${classes.FormControl} ${classes.fieldWithHelpInner}`}
                            fullWidth
                          >
                            <InputLabel id="integrationId-selection-label">
                              {i18n.t("queueModal.form.integrationId")}
                            </InputLabel>
                            <Select
                              label={i18n.t("queueModal.form.integrationId")}
                              name="integrationId"
                              value={selectedIntegration || ""}
                              onChange={handleChangeIntegration}
                              id="integrationId"
                              variant="outlined"
                              margin="dense"
                              placeholder={i18n.t("queueModal.form.integrationId")}
                              labelId="integrationId-selection-label"
                              MenuProps={{
                                classes: { paper: dropdownClasses.paper },
                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                transformOrigin: { vertical: "top", horizontal: "left" },
                                getContentAnchorEl: null,
                              }}
                            >
                              <MenuItem value={null}>{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                              {externalIntegrations.map((integration) => (
                                <MenuItem key={integration.id} value={integration.id}>
                                  {integration.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.integrationHelp")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={16} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>
                      </>
                    )}
                  </DialogContent>
                </TabPanel>
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"nps"}
                >
                  <DialogContent dividers>
                    {/* INFO BOX EXPLICANDO O NPS */}
                    <div className={classes.importInfoBox}>
                      <div className={classes.importInfoTitle}>
                        <Info size={15} strokeWidth={2.2} />
                        {i18n.t("whatsappModal.form.npsInfoTitle")}
                      </div>
                      <div className={classes.importInfoText}>
                        <span dangerouslySetInnerHTML={{ __html: i18n.t("whatsappModal.form.npsInfoText") }} />
                      </div>
                    </div>

                    {/* SEÇÃO: PESQUISA */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.npsSurveySectionTitle")}
                    </div>

                    {/* MENSAGEM DE AVALIAÇÃO */}
                    <div className={classes.fieldWithHelp}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.ratingMessage")}
                        multiline
                        minRows={4}
                        fullWidth
                        name="ratingMessage"
                        error={touched.ratingMessage && Boolean(errors.ratingMessage)}
                        helperText={touched.ratingMessage && errors.ratingMessage}
                        variant="outlined"
                        margin="dense"
                        className={classes.fieldWithHelpInner}
                      />
                      <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.ratingMessageDesc")}>
                        <span className={classes.helpIcon}>
                          <HelpCircle size={16} strokeWidth={2} />
                        </span>
                      </Tooltip>
                    </div>

                    {/* SEÇÃO: REGRAS DE ENVIO */}
                    <div className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.form.npsRulesSectionTitle")}
                    </div>

                    <Grid container spacing={2}>
                      {/* QUANTIDADE MÁXIMA DE TENTATIVAS */}
                      <Grid item xs={12} md={6}>
                        <div className={classes.fieldWithHelp}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.maxUseBotQueuesNPS")}
                            fullWidth
                            name="maxUseBotQueuesNPS"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            error={touched.maxUseBotQueuesNPS && Boolean(errors.maxUseBotQueuesNPS)}
                            helperText={touched.maxUseBotQueuesNPS && errors.maxUseBotQueuesNPS}
                            className={classes.fieldWithHelpInner}
                          />
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.maxUseBotQueuesNPSDesc")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={16} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>
                      </Grid>

                      {/* TIMEOUT PARA ENCERRAR */}
                      <Grid item xs={12} md={6}>
                        <div className={classes.fieldWithHelp}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.expiresTicketNPS")}
                            fullWidth
                            name="expiresTicketNPS"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            error={touched.expiresTicketNPS && Boolean(errors.expiresTicketNPS)}
                            helperText={touched.expiresTicketNPS && errors.expiresTicketNPS}
                            className={classes.fieldWithHelpInner}
                          />
                          <Tooltip arrow placement="top" title={i18n.t("whatsappModal.form.expiresTicketNPSDesc")}>
                            <span className={classes.helpIcon}>
                              <HelpCircle size={16} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </div>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </TabPanel>
                {/* Flowbuilder */}
                {showIntegrations && (
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"flowbuilder"}
                  >
                    <DialogContent dividers>
                      {/* INFO BOX — O QUE É FLUXO PADRÃO */}
                      <div className={classes.importInfoBox}>
                        <div className={classes.importInfoTitle}>
                          <Info size={15} strokeWidth={2.2} />
                          {i18n.t("whatsappModal.flowBuilder.infoTitle")}
                        </div>
                        <div className={classes.importInfoText}>
                          <span dangerouslySetInnerHTML={{ __html: i18n.t("whatsappModal.flowBuilder.infoText") }} />
                        </div>
                      </div>

                      {/* SEÇÃO: FLUXO DE BOAS-VINDAS */}
                      <div className={classes.sectionTitle}>
                        {i18n.t("whatsappModal.flowBuilder.welcomeFlow")}
                      </div>
                      <div className={classes.sectionDescription}>
                        {i18n.t("whatsappModal.flowBuilder.welcomeFlowDescription")}
                      </div>
                      <div className={classes.fieldWithHelp}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={`${classes.FormControl} ${classes.fieldWithHelpInner}`}
                          fullWidth
                        >
                          <InputLabel id="flowIdWelcome-selection-label">
                            {i18n.t("whatsappModal.flowBuilder.welcomeFlowLabel")}
                          </InputLabel>
                          <Select
                            name="flowIdWelcome"
                            value={flowIdWelcome || ""}
                            onChange={handleChangeFlowIdWelcome}
                            id="flowIdWelcome"
                            variant="outlined"
                            margin="dense"
                            label={i18n.t("whatsappModal.flowBuilder.welcomeFlowLabel")}
                            labelId="flowIdWelcome-selection-label"
                            MenuProps={{
                              classes: { paper: dropdownClasses.paper },
                              anchorOrigin: { vertical: "bottom", horizontal: "left" },
                              transformOrigin: { vertical: "top", horizontal: "left" },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value={null}>{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                            {webhooks.map(webhook => (
                              <MenuItem key={webhook.id} value={webhook.id}>
                                {webhook.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Tooltip arrow placement="top" title={i18n.t("whatsappModal.flowBuilder.welcomeFlowHelp")}>
                          <span className={classes.helpIcon}>
                            <HelpCircle size={16} strokeWidth={2} />
                          </span>
                        </Tooltip>
                      </div>

                      {/* SEÇÃO: FLUXO DE RESPOSTA PADRÃO */}
                      <div className={classes.sectionTitle}>
                        {i18n.t("whatsappModal.flowBuilder.defaultResponseFlow")}
                      </div>
                      <div className={classes.sectionDescription}>
                        {i18n.t("whatsappModal.flowBuilder.defaultResponseFlowDescription")}
                      </div>
                      <div className={classes.fieldWithHelp}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={`${classes.FormControl} ${classes.fieldWithHelpInner}`}
                          fullWidth
                        >
                          <InputLabel id="flowNotIdPhrase-selection-label">
                            {i18n.t("whatsappModal.flowBuilder.defaultResponseFlowLabel")}
                          </InputLabel>
                          <Select
                            name="flowNotIdPhrase"
                            value={flowIdNotPhrase || ""}
                            onChange={handleChangeFlowIdNotPhrase}
                            id="flowNotIdPhrase"
                            variant="outlined"
                            margin="dense"
                            label={i18n.t("whatsappModal.flowBuilder.defaultResponseFlowLabel")}
                            labelId="flowNotIdPhrase-selection-label"
                            MenuProps={{
                              classes: { paper: dropdownClasses.paper },
                              anchorOrigin: { vertical: "bottom", horizontal: "left" },
                              transformOrigin: { vertical: "top", horizontal: "left" },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value={null}>{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                            {webhooks.map(webhook => (
                              <MenuItem key={webhook.id} value={webhook.id}>
                                {webhook.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Tooltip arrow placement="top" title={i18n.t("whatsappModal.flowBuilder.defaultResponseFlowHelp")}>
                          <span className={classes.helpIcon}>
                            <HelpCircle size={16} strokeWidth={2} />
                          </span>
                        </Tooltip>
                      </div>
                    </DialogContent>
                  </TabPanel>
                )}
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"schedules"}
                >
                  {tab === "schedules" && (
                    <div className={classes.schedulesTabContainer}>
                      <SchedulesForm
                        loading={false}
                        initialValues={schedules}
                        showOutOfHoursMessage
                        initialOutOfHoursMessage={values.outOfHoursMessage || ""}
                        initialKeepFlowsOutOfHours={!!values.keepFlowsOutOfHours}
                        labelSaveButton={whatsAppId
                          ? i18n.t("whatsappModal.buttons.okEdit")
                          : i18n.t("whatsappModal.buttons.okAdd")}
                        onSubmit={(data, extras = {}) => {
                          schedulesRef.current = data;
                          setSchedules(data);
                          if (typeof extras.outOfHoursMessage === "string") {
                            setFieldValue("outOfHoursMessage", extras.outOfHoursMessage);
                          }
                          if (typeof extras.keepFlowsOutOfHours === "boolean") {
                            setFieldValue("keepFlowsOutOfHours", extras.keepFlowsOutOfHours);
                          }
                          setTimeout(() => {
                            submitForm();
                          }, 0);
                        }}
                      />
                    </div>
                  )}

                </TabPanel>
              </Paper>
              {tab !== "schedules" && (
                <ChatiaDialogActions
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  saveText={whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                />
              )}
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
